// Feature 2 — Instructor Assessment (teacher-defined evaluation). Conceptually SEPARATE from MY DEVELOPMENT.
// Read-only for students (server-enforced). Accessible: semantic tables, labelled inputs, visible focus,
// no color-only meaning. NOT an LMS gradebook — the grid is a management summary only.
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { cSchemes, cCreateScheme, cAssessConfig, cSetAssessConfig, cAssignmentAssessments,
  cGetStudentAssessment, cPutStudentAssessment, cCourseGradebook, cMyAssessment } from "@/lib/api";

const BRAND = "#8C3A2A";
const primary = `px-3 py-1.5 rounded-md text-[13px] text-white`;
const focus = "focus:outline-none focus:ring-2 focus:ring-[#8C3A2A]";
const fmtVal = (d) => {
  if (d.value == null || d.value === "") return "—";
  if (d.type === "percentage") return `${d.value}%`;
  if (d.type === "points") return `${d.value} / ${d.scale_config?.max ?? "?"}`;
  if (d.type === "numeric") return `${d.value} / ${d.scale_config?.max ?? 4}`;
  return `${d.value}`;
};

// ---------- STUDENT (read-only) ----------
export function StudentInstructorAssessment({ assignmentId }) {
  const [d, setD] = useState(null);
  useEffect(() => { let on = true; cMyAssessment(assignmentId).then((x) => on && setD(x)).catch(() => on && setD({ assessed: false })); return () => { on = false; }; }, [assignmentId]);
  if (!d || !d.assessed) return null;
  return (
    <section data-testid="instructor-assessment" aria-labelledby="ia-heading" className="mt-8">
      <h3 id="ia-heading" className="text-base font-semibold text-stone-900">Instructor Assessment</h3>
      <p className="mt-1 text-[13px] text-stone-600">Your instructor’s evaluation of your work. This is separate from My Development.</p>
      {d.dimensions.length > 0 && (
        <table data-testid="ia-table" className="mt-3 w-full border-collapse text-left text-[13px]">
          <caption className="sr-only">Instructor assessment by dimension</caption>
          <thead><tr className="border-b border-stone-200 text-[11px] uppercase tracking-wider text-stone-500">
            <th scope="col" className="py-2 pr-3">Dimension</th><th scope="col" className="py-2 pr-3">Assessment</th><th scope="col" className="py-2">Comment</th></tr></thead>
          <tbody>
            {d.dimensions.map((dim, i) => (
              <tr key={i} data-testid={`ia-dim-${i}`} className="border-b border-stone-100 align-top">
                <th scope="row" className="py-2 pr-3 font-medium text-stone-800">{dim.name}{dim.student_description ? <span className="block text-[11px] font-normal text-stone-400">{dim.student_description}</span> : null}</th>
                <td className="py-2 pr-3 text-stone-700">{fmtVal(dim)}</td>
                <td className="py-2 text-stone-600 whitespace-pre-wrap">{dim.comment || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {(d.overall_value || d.overall_comment) && (
        <div data-testid="ia-overall" className="mt-3 rounded-md border border-stone-200 bg-white p-3" style={{ borderTopColor: BRAND, borderTopWidth: 2 }}>
          <div className="text-[11px] font-mono uppercase tracking-wider text-stone-400">Overall</div>
          {d.overall_value && <p className="text-lg font-semibold text-stone-900">{d.overall_value}</p>}
          {d.overall_comment && <p className="mt-1 text-[13px] text-stone-700 whitespace-pre-wrap">{d.overall_comment}</p>}
        </div>
      )}
    </section>
  );
}

// ---------- TEACHER: assess one student × assignment ----------
export function TeacherAssessmentPanel({ courseId, assignmentId, studentId }) {
  const [cfg, setCfg] = useState(null);
  const [schemes, setSchemes] = useState([]);
  const [data, setData] = useState(null);
  const [vals, setVals] = useState({});
  const [overall, setOverall] = useState(""); const [oComment, setOComment] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const c = await cAssessConfig(assignmentId); setCfg(c);
    setSchemes((await cSchemes(courseId)).schemes.filter((s) => s.status !== "archived"));
    if (c.scheme_id) {
      const d = await cGetStudentAssessment(assignmentId, studentId); setData(d);
      const vm = {}; (d.assessment?.values || []).forEach((v) => { vm[v.dimension_id] = { value: v.value ?? "", comment: v.comment || "" }; });
      setVals(vm); setOverall(d.assessment?.overall_value || ""); setOComment(d.assessment?.overall_comment || "");
    } else { setData(null); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [assignmentId, studentId]);

  const attach = async (schemeId) => { await cSetAssessConfig(assignmentId, { scheme_id: schemeId, calculation_enabled: false }); toast.success("Scheme attached"); load(); };
  const quickScheme = async () => {
    const body = { name: "Simple Assessment", course_id: courseId, dimensions: [
      { name: "Quality of Thought", type: "numeric", scale_config: { min: 1, max: 4 }, student_visible: true, display_order: 0 },
      { name: "Organization", type: "category", scale_config: { categories: ["Beginning", "Developing", "Proficient", "Advanced"] }, student_visible: true, display_order: 1 },
      { name: "Evidence", type: "numeric", scale_config: { min: 1, max: 4 }, student_visible: true, display_order: 2 }] };
    const s = await cCreateScheme(body); await attach(s.id);
  };
  const setV = (id, k, v) => setVals((p) => ({ ...p, [id]: { ...(p[id] || {}), [k]: v } }));
  const save = async (status) => {
    setBusy(true);
    try {
      const values = (data.scheme?.dimensions || []).map((d) => ({ dimension_id: d.id, value: vals[d.id]?.value ?? null, comment: vals[d.id]?.comment || "" }));
      await cPutStudentAssessment(assignmentId, studentId, { overall_value: overall || null, overall_comment: oComment, values, status });
      toast.success(status === "released" ? "Released to student" : "Saved as draft"); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); } finally { setBusy(false); }
  };

  if (!cfg) return <p className="text-[13px] text-stone-400">Loading assessment…</p>;
  if (!cfg.scheme_id) return (
    <section data-testid="assess-no-scheme" className="mt-6 rounded-md border border-stone-200 bg-white p-4">
      <h3 className="text-base font-semibold text-stone-900">Instructor Assessment</h3>
      <p className="mt-1 text-[13px] text-stone-600">Attach an assessment scheme to this assignment to begin evaluating.</p>
      {schemes.length > 0 && (
        <div className="mt-3">
          <label htmlFor="scheme-pick" className="text-[12px] text-stone-600">Use an existing scheme</label>
          <select id="scheme-pick" data-testid="scheme-pick" className={`mt-1 block rounded-md border border-stone-300 p-2 text-[13px] ${focus}`} defaultValue="" onChange={(e) => e.target.value && attach(e.target.value)}>
            <option value="" disabled>Choose a scheme…</option>
            {schemes.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </div>
      )}
      <button data-testid="quick-scheme" className={`${primary} mt-3`} style={{ background: BRAND }} onClick={quickScheme}>Create a simple scheme</button>
    </section>
  );

  const dims = data?.scheme?.dimensions || [];
  return (
    <section data-testid="assess-panel" aria-labelledby="assess-h" className="mt-6 rounded-md border border-stone-200 bg-white p-4">
      <div className="flex items-center justify-between">
        <h3 id="assess-h" className="text-base font-semibold text-stone-900">Instructor Assessment</h3>
        <span className="text-[11px] font-mono uppercase tracking-wider text-stone-400" data-testid="assess-status">{data?.assessment?.status || "not started"}</span>
      </div>
      <p className="mt-1 text-[12px] text-stone-500">Scheme: {data?.scheme?.name}</p>
      <div className="mt-3 space-y-3">
        {dims.map((d) => (
          <div key={d.id} data-testid={`assess-dim-${d.id}`}>
            <label htmlFor={`v-${d.id}`} className="text-[13px] font-medium text-stone-800">{d.name}{!d.student_visible && <span className="ml-1 text-[10px] uppercase text-amber-700">(teacher only)</span>}</label>
            {d.type === "category" ? (
              <select id={`v-${d.id}`} className={`mt-1 block w-full max-w-xs rounded-md border border-stone-300 p-2 text-[13px] ${focus}`} value={vals[d.id]?.value ?? ""} onChange={(e) => setV(d.id, "value", e.target.value)}>
                <option value="">—</option>
                {(d.scale_config?.categories || []).map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            ) : (
              <input id={`v-${d.id}`} type={d.type === "letter" ? "text" : "number"} className={`mt-1 block w-40 rounded-md border border-stone-300 p-2 text-[13px] ${focus}`}
                value={vals[d.id]?.value ?? ""} onChange={(e) => setV(d.id, "value", e.target.value)}
                placeholder={d.type === "numeric" ? `${d.scale_config?.min ?? 1}–${d.scale_config?.max ?? 4}` : d.type === "percentage" ? "0–100" : d.type === "points" ? `0–${d.scale_config?.max ?? ""}` : "e.g. B+"} />
            )}
            <input aria-label={`Comment for ${d.name}`} className={`mt-1 block w-full rounded-md border border-stone-200 p-2 text-[12px] ${focus}`} placeholder="Comment (optional)" value={vals[d.id]?.comment ?? ""} onChange={(e) => setV(d.id, "comment", e.target.value)} />
          </div>
        ))}
        <div className="border-t border-stone-100 pt-3">
          <label htmlFor="overall-v" className="text-[13px] font-medium text-stone-800">Overall (optional)</label>
          <input id="overall-v" data-testid="assess-overall" className={`mt-1 block w-40 rounded-md border border-stone-300 p-2 text-[13px] ${focus}`} value={overall} onChange={(e) => setOverall(e.target.value)} placeholder="e.g. B+ or Proficient" />
          <textarea aria-label="Overall comment" data-testid="assess-overall-comment" className={`mt-2 block w-full rounded-md border border-stone-200 p-2 text-[13px] ${focus}`} rows={2} value={oComment} onChange={(e) => setOComment(e.target.value)} placeholder="Overall comment (optional)" />
        </div>
      </div>
      <div className="mt-3 flex gap-2">
        <button data-testid="assess-save-draft" disabled={busy} className={`rounded-md border border-stone-300 px-3 py-1.5 text-[13px] text-stone-700 ${focus}`} onClick={() => save("draft")}>Save draft</button>
        <button data-testid="assess-release" disabled={busy} className={primary} style={{ background: BRAND }} onClick={() => save("released")}>Release to student</button>
      </div>
    </section>
  );
}

// ---------- TEACHER: course gradebook (management summary) ----------
export function CourseGradebook({ courseId }) {
  const [g, setG] = useState(null);
  useEffect(() => { let on = true; cCourseGradebook(courseId).then((x) => on && setG(x)); return () => { on = false; }; }, [courseId]);
  if (!g) return <p className="text-[13px] text-stone-400">Loading gradebook…</p>;
  return (
    <div data-testid="course-gradebook">
      <h1 className="font-serif text-3xl text-stone-900">Assessments</h1>
      <p className="mt-1 text-[13px] text-stone-600">A summary of instructor assessments you have entered. This is not a substitute for My Development.</p>
      <div className="mt-4 overflow-x-auto">
        <table className="w-full border-collapse text-left text-[13px]">
          <caption className="sr-only">Instructor assessment status per student and assignment</caption>
          <thead><tr className="border-b border-stone-200 text-[11px] uppercase tracking-wider text-stone-500">
            <th scope="col" className="py-2 pr-3">Student</th>
            {g.assignments.map((a) => <th key={a.id} scope="col" className="py-2 pr-3">{a.title}</th>)}
          </tr></thead>
          <tbody>
            {g.rows.map((r) => (
              <tr key={r.student_id} className="border-b border-stone-100">
                <th scope="row" className="py-2 pr-3 font-medium text-stone-800">{r.name}</th>
                {r.cells.map((c, i) => <td key={i} className="py-2 pr-3 text-stone-700">{c.overall_value || c.state}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
